/* STEP 3 — Rates. Two modes (as on master), both feeding the real CSV upload:
     • Download & upload — download a system-format template, edit offline, re-upload.
     • Edit in UI — open the RateMatrixDialog seeded from a starter grid, edit the
       zone × weight-slab rates inline (Preview-Δ below), then on Next the edited rows
       are serialized to a WEIGHT_PRICE CSV and pushed through the real upload.
   The backend accepts a CSV (type=WEIGHT_PRICE); a future effective date stages it
   pending (daily cron flips it live), today/past replaces the current card. */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import {FileDrop, RateMatrixDialog, Badge, fmtDate, useToast} from '../components'
import {pricingCsv} from '../dummyData'
import {buildSeededRates, deltaPill, todayIso} from './wizardUtils'
import DeltaPanel from './DeltaPanel'
import WizardFooter from './WizardFooter'

const isChanged = (r) => r.basePrice == null || (r.price !== '' && r.price != null && +r.price !== +r.basePrice)

export default function StepRates({U, onSetRateFile, onSetEff, onBack, onNext}) {
  const toast = useToast()
  const [mode, setMode] = useState('upload')
  const [rows, setRows] = useState(() => buildSeededRates(U.code).rows)
  const [editOpen, setEditOpen] = useState(false)

  const changed = (rows || []).filter(isChanged)

  const validate = () => {
    if (!U.effectiveFrom || U.effectiveFrom < todayIso()) {
      toast('Pick an effective date — today or later.', 'error')
      return false
    }
    if (mode === 'upload') {
      if (!(U.rateFile instanceof File)) {
        toast('Upload the rate-card CSV to continue.', 'error')
        return false
      }
      return true
    }
    // edit mode — every slab needs a price > 0, then serialize to CSV for the real upload
    const bad = (rows || []).filter((r) => !(parseFloat(r.price) > 0)).length
    if (!rows || !rows.length || bad) {
      toast((bad || 'All') + ' slab price(s) are empty or zero — every slab needs a price > 0.', 'error')
      return false
    }
    const csv = pricingCsv(rows)
    onSetRateFile(new File([csv], (U.code || 'rates') + '-rate-card.csv', {type: 'text/csv'}))
    return true
  }

  return (
    <>
      <div className='card card-pad' style={{marginBottom: 16}}>
        <div
          className='row between center'
          style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8}}
        >
          <h3 style={{margin: 0}}>New rates</h3>
          <span className='muted' style={{fontSize: 12}}>
            <span className='mono'>{U.code}</span> · effective <b>{fmtDate(U.effectiveFrom)}</b>
          </span>
        </div>
        <p className='sub' style={{margin: '6px 0 14px'}}>
          Revise the zone × weight-slab rates two ways — both feed the same upload and the delta
          preview below.
        </p>

        <div className='field' style={{marginBottom: 16, maxWidth: 300}}>
          <label>
            Effective from <span className='req'>*</span>
          </label>
          <input
            type='date'
            className='input'
            value={U.effectiveFrom}
            min={todayIso()}
            onChange={(e) => onSetEff(e.target.value)}
          />
          <div className='help'>
            <EventOutlinedIcon />
            Takes effect <b>{fmtDate(U.effectiveFrom)}</b> — current rates stay live until then.
          </div>
        </div>

        <div className='field' style={{marginBottom: 14}}>
          <label>How do you want to revise rates?</label>
          <div className='grid' style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10}}>
            {[
              ['upload', 'Download & upload', 'Download the current rate card in the system format, edit offline, then re-upload — the format is preserved.'],
              ['edit', 'Edit in UI', 'Open a preview window and edit the current → new rates inline; every change is amber-highlighted.'],
            ].map(([key, title, desc]) => {
              const on = mode === key
              return (
                <label
                  key={key}
                  className='checkrow optcard'
                  style={{
                    border: '1px solid ' + (on ? 'var(--primary)' : 'var(--border)'),
                    borderRadius: 8,
                    padding: 12,
                    alignItems: 'flex-start',
                    margin: 0,
                    background: on ? 'var(--primary-l)' : undefined,
                  }}
                >
                  <input
                    type='radio'
                    name='rateMode'
                    value={key}
                    checked={on}
                    onChange={() => setMode(key)}
                    style={{marginTop: 2}}
                  />
                  <div>
                    <div style={{fontWeight: 600, fontSize: 13.5}}>{title}</div>
                    <div className='muted' style={{fontSize: 12}}>{desc}</div>
                  </div>
                </label>
              )
            })}
          </div>
        </div>

        {mode === 'upload' ? (
          <>
            <FileDrop
              accept='.csv,.xlsx'
              maxMB={10}
              title='Drag & drop the revised rate card'
              hint='Accepted: .csv, .xlsx · max 10 MB · 19-column WEIGHT_PRICE format'
              sampleName='rate-card-template.csv'
              sampleLabel='Download current rate card (template)'
              sampleContent={pricingCsv([], true)}
              onFile={(file) => {
                onSetRateFile(file)
                toast(file.name + ' attached.', 'success')
              }}
              onError={(msg) => toast(msg, 'error')}
            />
            {U.rateFile ? (
              <div className='fileitem' style={{marginTop: 10}}>
                <span className='ic'>
                  <InsertDriveFileOutlinedIcon fontSize='small' />
                </span>
                <div style={{flex: 1}}>
                  <div style={{fontWeight: 600}}>{U.rateFile.name}</div>
                  <div className='hlp'>{(U.rateFile.size / 1024).toFixed(1)} KB · ready to upload</div>
                </div>
                <Badge status='uploaded' label='Attached' />
              </div>
            ) : null}
          </>
        ) : (
          <div
            className='row between center'
            style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10}}
          >
            <span className='muted' style={{fontSize: 12.5}}>
              {changed.length
                ? changed.length + ' slab' + (changed.length > 1 ? 's' : '') + ' edited — review the delta below.'
                : 'Open the editor and set the new zone × weight-slab rates.'}
            </span>
            <button type='button' className='btn btn-primary btn-sm' onClick={() => setEditOpen(true)}>
              <TableChartOutlinedIcon style={{width: 15, height: 15}} />
              {changed.length ? 'Edit rates' : 'Open rate editor'}
            </button>
          </div>
        )}

        <div className='warnbox info' style={{margin: '12px 0 0'}}>
          <InfoOutlinedIcon />
          <div>
            A <b>future</b> effective date stages this as a <b>pending</b> rate card — it goes live via
            the daily cron on that date. Today or a past date <b>replaces</b> the current card right away.
          </div>
        </div>
      </div>

      <DeltaPanel title='Preview Δ — what changed vs current' count={changed.length} emptyMsg='No rate changes yet — open the editor to set new prices.'>
        {changed.map((r, i) => (
          <div key={i} className='delta-row'>
            <span className='dz'>Zone {r.zone} · {r.min}-{r.max} g</span>
            <span className='old cut'>{r.basePrice != null ? '₹' + r.basePrice : '—'}</span>
            <span className='arr'><ArrowForwardIcon style={{width: 14, height: 14}} /></span>
            <span className='nw'>{r.price !== '' && r.price != null ? '₹' + r.price : '—'}</span>
            {r.basePrice == null ? (
              <span className='mini-pill mp-green'>NEW</span>
            ) : (
              <span className='muted' style={{fontSize: 11.5}}>{deltaPill(r.basePrice, r.price)}</span>
            )}
          </div>
        ))}
      </DeltaPanel>

      <RateMatrixDialog
        open={editOpen}
        rows={rows || []}
        title={'Edit rates — ' + (U.code || '')}
        subtitle='Set the new price per zone × weight slab. Edited cells are highlighted.'
        onClose={() => setEditOpen(false)}
        onConfirm={({rows: next}) => {
          setRows(next)
          setEditOpen(false)
          toast('Rates updated — review the delta below.', 'success')
        }}
      />

      <WizardFooter
        showBack
        last={false}
        onBack={onBack}
        onNext={() => {
          if (validate()) onNext()
        }}
      />
    </>
  )
}

StepRates.propTypes = {
  U: PropTypes.object,
  onSetRateFile: PropTypes.func,
  onSetEff: PropTypes.func,
  onBack: PropTypes.func,
  onNext: PropTypes.func,
}
